import {
  InteractionStepType,
  ReferenceText,
  InteractionStepConfig,
  IndexedInteractionStepResponse,
  PromptInputStepParams,
  StepParamsForType,
  StepParamsForTypeWithStage,
} from '@scale/shared/types/chat';
import { IMultiTurnThreadHistory } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ThreadHistory.interface';
import { extractPrefilledMessages } from '@scale/shared/chat/response/validation';
import { ResponseTemplatePath } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ResponseTemplateCreator/ResponseTemplatePath';
import { isConditionallyVisible, isStepVisible } from '@scale/shared/chat/api';
import PromptInputViewComponent from './PromptInputTaskComponent';
import PromptInputConfigurationComponent from './PromptInputConfigurationComponent';
import StepDescriptor from '../stepDescriptor/StepDescriptor';
import { registerStepDescriptor } from '../stepDescriptor/stepDescriptorContainer';
import { validateRequiredReferenceTextsAndAttachments } from './utils';
import { ReviewLevel } from '@scale/shared-types';
import { schema } from './validation';

export class PromptInputDescriptor extends StepDescriptor<InteractionStepType.PromptInput> {
  type = InteractionStepType.PromptInput;

  title = InteractionStepConfig[InteractionStepType.PromptInput].title;
  description = InteractionStepConfig[InteractionStepType.PromptInput].description;

  validationSchema = schema;

  ViewComponent = PromptInputViewComponent;
  ConfigurationComponent = PromptInputConfigurationComponent;

  isResponseValid(
    response: IndexedInteractionStepResponse,
    stepParams: StepParamsForTypeWithStage<InteractionStepType.PromptInput>,
    history: IMultiTurnThreadHistory,
    reviewLevel?: ReviewLevel,
  ) {
    if (
      !isStepVisible(stepParams, reviewLevel) ||
      !isConditionallyVisible(
        stepParams,
        history.responses,
        response.index,
        history.threadParams.templateVariables,
        reviewLevel,
      )
    ) {
      return { canSubmit: true, submitTooltip: '' };
    }

    if (!response.output || response.output.length === 0) {
      return { canSubmit: false, submitTooltip: 'Some prompts have not been completed.' };
    }

    /*
     * Note: turnIndex must be a 0-indexed representation of the turn number
     * stepParams.turn is actually two behind the turn number, so we add one to get a 0-indexed representation
     */
    const { canSubmit, submitTooltip } = validateRequiredReferenceTextsAndAttachments(
      response,
      history.threadParams,
      stepParams.turn + 1,
    );

    if (!canSubmit) {
      return { canSubmit, submitTooltip };
    }

    return { canSubmit: true, submitTooltip: '' };
  }

  getResponseTemplatePaths(
    stepParams: StepParamsForType<InteractionStepType.PromptInput>,
    history: IMultiTurnThreadHistory,
  ) {
    const { useReferenceText, useAttachments } = history.threadParams;
    const paths: ResponseTemplatePath[] = [
      {
        path: 'output',
        label: 'Prompt',
      },
    ];
    if (useReferenceText) {
      paths.push({
        path: 'context.referenceTexts',
        label: 'Reference Texts',
        expectsArray: true,
        expectedSchema: [
          {
            content: 'test',
            url: 'https://test.com',
          },
        ],
      });
    }
    if (useAttachments) {
      paths.push({
        path: 'context.attachments',
        label: 'Attachments',
        isAttachments: true,
      });
    }
    return paths;
  }

  generateEmptyResponse(
    stepParams: StepParamsForType<InteractionStepType.PromptInput>,
    history: IMultiTurnThreadHistory,
  ) {
    const { initialMessages } = history.threadParams;
    const { taskingUserMessages } = extractPrefilledMessages(initialMessages);
    let output = '';
    if (
      taskingUserMessages.length > 0 &&
      taskingUserMessages[0].content &&
      taskingUserMessages[0].content!.length > 0
    ) {
      output = taskingUserMessages[0].content!;
    }
    let referenceTexts: ReferenceText[] | undefined = undefined;
    if (taskingUserMessages.length > 0 && taskingUserMessages[0].referenceText) {
      referenceTexts = [
        {
          content: taskingUserMessages[0].referenceText,
          url: taskingUserMessages[0].referenceUrl,
        },
      ];
    }
    return {
      type: InteractionStepType.PromptInput,
      context: {
        referenceTexts,
      },
      output,
    };
  }

  getConfigurationPreviewTags(stepParams: StepParamsForType<InteractionStepType.PromptInput>) {
    const tags = super.getConfigurationPreviewTags(stepParams);
    const promptInputParams = stepParams as PromptInputStepParams;
    if (
      promptInputParams.params.prompt_constraints &&
      promptInputParams.params.prompt_constraints.length > 0
    ) {
      tags.push({
        value: 'Prompt Constraints',
        variant: 'info',
        icon: 'pen',
        tooltipText: 'This step contains prompt constraints',
      });
    }
    return tags;
  }
}

registerStepDescriptor({
  type: InteractionStepType.PromptInput,
  descriptor: new PromptInputDescriptor(),
});
